/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { useSearchHistory } from '@/rest/hooks/use-search-history';

import { type components } from '@marketplace/gather-api/types/marketplace-vehicle-catalog-mgmt';

type SearchHistory = components['schemas']['SearchHistory'];

type FlatSearchHistory =
  | {
      data: null;
      status: 'pending';
      error: null;
    }
  | {
      data: null;
      status: 'error';
      error: Error;
    }
  | {
      data: SearchHistory[];
      status: 'success';
      error: null;
    };

/**
 * Flattens/Reduce the paginated response from the useSearchHistory hook into a single
 * array that will only contain a max of 3 entries.
 */
export function useFlatSearchHistory(): FlatSearchHistory {
  const state = useSearchHistory();

  // NOTE: When the query is disabled (e.g. no userId), React Query reports
  // status 'pending' with fetchStatus 'idle'. Treat this as a success with
  // empty data so the UI doesn't show loading skeletons indefinitely.
  if (state.status === 'pending' && state.fetchStatus === 'idle') {
    return {
      data: [],
      status: 'success',
      error: null,
    };
  }

  if (state.status === 'pending') {
    return {
      data: null,
      status: state.status,
      error: null,
    };
  } else if (state.status === 'error') {
    return {
      data: null,
      status: state.status,
      error: state.error ?? new Error('there was an unexpected error'),
    };
  }

  return {
    // NOTE: Filter out entries with no query or an empty/whitespace-only query before slicing
    // so we always get up to 3 valid chips. This replicates the same behavior as the Flutter app
    // which converts empty query strings to null and excludes them from the search history display.
    data: state.data.items.filter((item) => item.query.trim().length > 0).slice(0, 3) ?? [], // 🏌
    status: state.status,
    error: null,
  };
}
